import React from "react";
import { ActionMeta, GroupBase, SingleValue, StylesConfig } from "react-select";
import { Text } from "./text";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { boolean } from "zod";
import { components as RSComponents } from "react-select";

interface CustomSelectComponents {
  DropdownIndicator: typeof RSComponents.DropdownIndicator | (() => null);
  ClearIndicator: typeof RSComponents.ClearIndicator | (() => null);
}

interface ReactSelectPaginateProps {
  loadOptions: LoadOptions<
    { value: string; label: string },
    GroupBase<{ value: string; label: string }>,
    { page: number }
  >;
  value: { value: string; label: string } | null;
  onChange?: (
    newValue: SingleValue<{ value: string; label: string }>,
    actionMeta: ActionMeta<{ value: string; label: string }>
  ) => void;
  debounce?: number;
  maxLength?: number;
  placeholder?: string;
  maxMenuHeight?: number;
  isDisabled?: boolean;
  isClearable?: boolean;
  label?: string;
  inputClassName?: string;
  styles?: StylesConfig<{ value: string; label: string }, false>;
  components?: CustomSelectComponents;
  props?: any;
}

const ReactSelectPaginate: React.FC<ReactSelectPaginateProps> = ({
  loadOptions = () =>
    Promise.resolve({
      options: [],
      hasMore: false,
      additional: { page: 1 },
      params: { page: 1 }
    }),
  value,
  onChange,
  label,
  debounce,
  maxLength = 255,
  maxMenuHeight = 300,
  placeholder = "Select an option",
  ...props
}) => {
  return (
    <div className="w-full h-full flex flex-col">
      <Text className="text-body-1-strong mb-[4px]">{label}</Text>
      <AsyncPaginate
        id="companyId"
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        maxMenuHeight={maxMenuHeight}
        isClearable={true}
        components={{
          IndicatorSeparator: () => null
        }}
        isSearchable={true}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => option.label}
        placeholder={placeholder}
        debounceTimeout={debounce || 500}
        additional={{
          page: 0
        }}
        {...props}
        styles={{
          control: (base) => ({
            ...base,
            boxShadow: "none",
            padding: "6px 4px 6px 4px",
            border: "1px solid #f2f2f2",
            ":hover": {
              border: "1px solid #f2f2f2"
            },
            backgroundColor: "transparent",
            borderRadius: "0.5rem"
          }),
          menu: (base) => ({
            ...base,
            // Max height for the dropdown
            overflowY: "auto", // Scrollable menu
            width: "100%", // Ensure menu is same width as the input
            marginTop: "4px", // Reduced space between the select box and dropdown
            zIndex: 50,
            padding: "0.5rem",
            borderRadius: "0.5rem"
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: "8px 4px" // Reduce padding for clear button
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "8px 4px" // Reduce padding for dropdown arrow button
          }),
          singleValue: (base) => ({
            ...base,
            overflow: "hidden", // Hide overflowed text
            textOverflow: "ellipsis", // Ellipsis on overflow
            whiteSpace: "nowrap", // Prevent text wrapping
            maxWidth: "100%" // Allow the text to take up available space
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? "#D9D9D9" : "white",
            ":hover": {
              backgroundColor: state.isSelected ? "#D9D9D9" : "#f2f2f2"
            },
            color: "black",
            overflow: "hidden", // Hide overflowed text
            textOverflow: "ellipsis", // Ellipsis on overflow
            whiteSpace: "nowrap", // Prevent text wrapping
            maxWidth: "100%",
            fontSize: "0.875rem",
            lineHeight: "1.2rem",
            padding: "0.5rem 1rem"
          }),

          ...(props.styles || {})
        }}
      />
    </div>
  );
};

export default ReactSelectPaginate;
